'use client';

import { c as _c } from "react/compiler-runtime";
import { getRouteStopBiasCoordinates } from '@repo/geolocation';
import type { TimeType } from '@repo/features/time-picker';
import { useAppForm, useSelector } from '@repo/form';
import { type UseHotkeyDefinition, useHotkeys } from '@repo/shortcuts';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Button, ButtonGroup, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Field, FieldContent, FieldError, FieldLabel, Input, Kbd, KbdGroup, Select, SelectContent, SelectItem, SelectPortal, SelectTrigger, SelectValue, Separator, Skeleton } from '@repo/ui/components';
import { CircleHelp, Plus, X } from '@repo/ui/icons';
import type { FocusEvent, KeyboardEvent, ReactNode, RefObject } from 'react';
import { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import { type CompanyPickerCompany, FormCompanyPicker } from '@context/companies';
import { useCurrencies } from '@/app/@protected/_hooks/use-currencies.ts';
import { calendarDayNumber, copyLocalCalendarDate } from './_utils/calendar-date.ts';
import { buildQuickCreatePublishValues, getQuickCreateInitialValues } from './_utils/publish-values.ts';
import { canDeleteStop, canMoveStop, deleteStop, getEarliestAllowedDate, insertAfterActiveStop, moveStop, type QuickCreateStop, toggleStopType } from './_utils/route-model.ts';
import { getQuickCreateChronologyValidationErrors, getQuickCreateFieldValidationErrors, getQuickCreateValidationIssues } from './_utils/validation.ts';
import type { QuickCreateLoadDefaults, QuickCreateValidationIssue, QuickCreateValues } from './_utils/values.ts';
import { getStopTimeValue, type QuickCreateLocationCreationRequest, type QuickCreateLocationEditRequest, QuickCreateStopField } from './quick-create-stop-field.tsx';

/** The company shape needed by the registered CompanyPicker. */
export type QuickCreateCompany = CompanyPickerCompany;
export type QuickCreateCompanyCreationRequest = {
  kind: 'customer' | 'end-customer';
  onCreated: (_company: QuickCreateCompany) => void;
  organisationId: null | number;
  search: string;
};
export type QuickCreateCompanySource = {
  companies: readonly QuickCreateCompany[];
  error?: null | string;
  onRetry?: () => void;
  onSearchChange?: (_search: string) => void;
  selectedCompany?: null | QuickCreateCompany;
  status?: 'loading' | 'pending' | 'ready';
};
export type { QuickCreateLoadDefaults, QuickCreateValues };
export type { QuickCreateLocationCreationRequest, QuickCreateLocationEditRequest };
export type QuickCreateLoadDialogProps = {
  currentUser: null | {
    id: number;
  };
  customerCompanies: QuickCreateCompanySource;
  customerRiskBanner?: ReactNode;
  endCustomerCompanies: QuickCreateCompanySource;
  initialValues?: QuickCreateLoadDefaults;
  nestedSheetOutlets?: ReactNode;
  onCreateCompany?: (_request: QuickCreateCompanyCreationRequest) => void;
  onCreateLocation?: (_request: QuickCreateLocationCreationRequest) => void;
  onCustomerChange?: (_company: null | QuickCreateCompany) => void;
  onEditLocation?: (_request: QuickCreateLocationEditRequest) => void;
  onOpenChange: (_open: boolean) => void;
  onOrganisationChange?: (_organisationId: null | number) => void;
  onPublish: (_values: QuickCreateValues) => Promise<void> | void;
  open: boolean;
  organisation: {
    editable: boolean;
    options: readonly QuickCreateOrganisation[];
  };
  pending?: boolean;
  publishError?: null | string;
};
export type QuickCreateOrganisation = {
  id: number;
  name: string;
};
const TIME_TYPES = ['asap', 'at', 'by', 'between', 'tbc'] as const satisfies readonly TimeType[];
type QuickCreateStopCandidate = Omit<QuickCreateStop, 'type'> & {
  type: string;
};
const isQuickCreateStop = (stop: QuickCreateStopCandidate): stop is QuickCreateStop => stop.type === 'collection' || stop.type === 'delivery';
const getSelectedCompany = (companyId: null | number, createdCompany: null | QuickCreateCompany, source: QuickCreateCompanySource) => {
  if (companyId == null) return null;
  if (createdCompany?.id === companyId) return createdCompany;
  if (source.selectedCompany?.id === companyId) return source.selectedCompany;
  return source.companies.find(company => company.id === companyId) ?? null;
};
const isThreePlBroker = (company: null | QuickCreateCompany) => company?.isBroker === true && company.isMarketplace === true;
const QuickCreateCurrencySelect = t0 => {
  const $ = _c(14);
  const {
    disabled,
    onChange,
    value
  } = t0;
  const currencies = useCurrencies();
  let t1;
  if ($[0] !== onChange) {
    t1 = next => onChange(next ?? "");
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectValue data-sentry-element="SelectValue" data-sentry-source-file="quick-create-load-dialog.tsx" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== disabled) {
    t3 = <SelectTrigger aria-label="Rate currency" className="w-24 not-data-disabled:hover:bg-muted" disabled={disabled} data-sentry-element="SelectTrigger" data-sentry-source-file="quick-create-load-dialog.tsx">{t2}</SelectTrigger>;
    $[3] = disabled;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== currencies) {
    t4 = currencies.map(_temp);
    $[5] = currencies;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <SelectPortal data-sentry-element="SelectPortal" data-sentry-source-file="quick-create-load-dialog.tsx"><SelectContent align="end" className="min-w-32" data-sentry-element="SelectContent" data-sentry-source-file="quick-create-load-dialog.tsx">{t4}</SelectContent></SelectPortal>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t3 || $[11] !== t5 || $[12] !== value) {
    t6 = <Select onValueChange={t1} value={value} data-sentry-element="Select" data-sentry-component="QuickCreateCurrencySelect" data-sentry-source-file="quick-create-load-dialog.tsx">{t3}{t5}</Select>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t5;
    $[12] = value;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
const StopGap = t0 => {
  const $ = _c(10);
  const {
    afterStopNumber,
    beforeStopNumber,
    disabled,
    onAdd
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Separator className="flex-1" data-sentry-element="Separator" data-sentry-source-file="quick-create-load-dialog.tsx" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `Add Stop between Stops ${beforeStopNumber} and ${afterStopNumber}`;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Plus data-sentry-element="Plus" data-sentry-source-file="quick-create-load-dialog.tsx" />;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Kbd data-sentry-element="Kbd" data-sentry-source-file="quick-create-load-dialog.tsx">SA</Kbd>;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== disabled || $[4] !== onAdd || $[5] !== t2) {
    t5 = <Button aria-label={t2} disabled={disabled} onClick={onAdd} size="sm" type="button" variant="outline" data-sentry-element="Button" data-sentry-source-file="quick-create-load-dialog.tsx">{t3}Add Stop{t4}</Button>;
    $[3] = disabled;
    $[4] = onAdd;
    $[5] = t2;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Separator className="flex-1" data-sentry-element="Separator" data-sentry-source-file="quick-create-load-dialog.tsx" />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5) {
    t7 = <div className="flex items-center gap-2" data-sentry-component="StopGap" data-sentry-source-file="quick-create-load-dialog.tsx">{t1}{t5}{t6}</div>;
    $[8] = t5;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
};

// Outside the component because the React Compiler can't lower try/finally yet.
const whileFlagged = async (flag: {
  current: boolean;
}, run: () => Promise<void>) => {
  flag.current = true;
  try {
    await run();
  } finally {
    flag.current = false;
  }
};

// `false` means the shortcut did nothing, so the key keeps its default behaviour.
type ShortcutResult = boolean | Promise<void> | void;

// Mounted inside the dialog so the hotkeys bind to its element, never the document.
const QuickCreateHotkeys = t0 => {
  const $ = _c(4);
  const {
    hotkeys,
    sequences,
    target
  } = t0;
  let t1;
  if ($[0] !== target) {
    t1 = {
      ignoreInputs: false,
      preventDefault: false,
      stopPropagation: false,
      target
    };
    $[0] = target;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useHotkeys(hotkeys, t1);
  let t2;
  if ($[2] !== target) {
    t2 = {
      preventDefault: false,
      stopPropagation: false,
      target,
      timeout: 700
    };
    $[2] = target;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useHotkeys(sequences, t2);
  return null;
};
const isPopupSurface = (target: EventTarget | null) => target instanceof HTMLElement && Boolean(target.closest('[aria-expanded="true"], [data-quick-create-menu], [data-slot="combobox-content"], [data-slot="command"], [data-slot="popover-content"], [data-slot="select-content"], [data-radix-popper-content-wrapper], [role="listbox"], [role="menu"]'));
const isTextEntryTarget = (target: HTMLElement) => Boolean(target.closest('[contenteditable="true"], input, select, textarea'));
const isArrowNavigationBlockedTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return true;
  return isPopupSurface(target) || isTextEntryTarget(target);
};
export const QuickCreateLoadDialog = t0 => {
  const $ = _c(160);
  const {
    currentUser,
    customerCompanies,
    customerRiskBanner,
    endCustomerCompanies,
    initialValues,
    nestedSheetOutlets,
    onCreateCompany,
    onCreateLocation,
    onCustomerChange,
    onEditLocation,
    onOpenChange,
    onOrganisationChange,
    onPublish,
    open,
    organisation,
    pending: t1,
    publishError: t2
  } = t0;
  const pending = t1 === undefined ? false : t1;
  const publishError = t2 === undefined ? null : t2;
  let t3;
  if ($[0] !== currentUser || $[1] !== initialValues) {
    t3 = () => getQuickCreateInitialValues({
      currentUser,
      initialValues
    });
    $[0] = currentUser;
    $[1] = initialValues;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [formDefaults, setFormDefaults] = useState(t3);
  const submitAttemptedRef = useRef(false);
  let t4;
  if ($[3] !== onPublish) {
    t4 = t5 => {
      const {
        value
      } = t5;
      return onPublish(buildQuickCreatePublishValues(value));
    };
    $[3] = onPublish;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      onChange: t6 => {
        const {
          value: value_0
        } = t6;
        return {
          fields: submitAttemptedRef.current ? getQuickCreateFieldValidationErrors(value_0) : getQuickCreateChronologyValidationErrors(value_0)
        };
      }
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== formDefaults || $[7] !== t4) {
    t6 = {
      defaultValues: formDefaults,
      onSubmit: t4,
      validators: t5
    };
    $[6] = formDefaults;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const form = useAppForm(t6);
  const values = useSelector(form.store, _temp2);
  const isDirty = useSelector(form.store, _temp3);
  const [activeStopId, setActiveStopId] = useState(null);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [endCustomerPickerOpen, setEndCustomerPickerOpen] = useState(false);
  const [endCustomerSearch, setEndCustomerSearch] = useState("");
  const [createdCustomer, setCreatedCustomer] = useState(null);
  const [createdEndCustomer, setCreatedEndCustomer] = useState(null);
  const [discardConfirmationOpen, setDiscardConfirmationOpen] = useState(false);
  const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [timeMenuStopId, setTimeMenuStopId] = useState(null);
  const timeMenuStopIdRef = useRef(null);
  const previousOpenRef = useRef(open);
  const helpReturnFocusRef = useRef(null);
  const dialogRef = useRef(null);
  const isPublishingRef = useRef(false);
  const pendingPostCommitFocusRef = useRef(null);
  const locationTriggerRefs = useRef(new Map());
  const dateTriggerRefs = useRef(new Map());
  const customerTriggerRef = useRef(null);
  const endCustomerTriggerRef = useRef(null);
  const organisationTriggerRef = useRef(null);
  const vehicleTriggerRef = useRef(null);
  const weightInputRef = useRef(null);
  const rateInputRef = useRef(null);
  const focusStopFrameRef = useRef(null);
  let selectedCustomer;
  let selectedEndCustomer;
  let t7;
  if ($[9] !== createdCustomer || $[10] !== createdEndCustomer || $[11] !== customerCompanies || $[12] !== endCustomerCompanies || $[13] !== values.company_id || $[14] !== values.end_customer_company_id) {
    selectedCustomer = getSelectedCompany(values.company_id, createdCustomer, customerCompanies);
    let t8;
    if ($[18] !== createdEndCustomer || $[19] !== endCustomerCompanies || $[20] !== values.end_customer_company_id) {
      t8 = getSelectedCompany(values.end_customer_company_id, createdEndCustomer, endCustomerCompanies);
      $[18] = createdEndCustomer;
      $[19] = endCustomerCompanies;
      $[20] = values.end_customer_company_id;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    selectedEndCustomer = t8;
    t7 = isThreePlBroker(selectedCustomer);
    $[9] = createdCustomer;
    $[10] = createdEndCustomer;
    $[11] = customerCompanies;
    $[12] = endCustomerCompanies;
    $[13] = values.company_id;
    $[14] = values.end_customer_company_id;
    $[15] = selectedCustomer;
    $[16] = selectedEndCustomer;
    $[17] = t7;
  } else {
    selectedCustomer = $[15];
    selectedEndCustomer = $[16];
    t7 = $[17];
  }
  const requiresEndCustomer = t7;
  const showEndCustomer = requiresEndCustomer || selectedCustomer?.isBroker === true && values.end_customer_company_id != null;
  const locationCompanyId = showEndCustomer ? values.end_customer_company_id : values.company_id;
  let t8;
  if ($[22] !== hasSubmitAttempted || $[23] !== values) {
    t8 = hasSubmitAttempted ? getQuickCreateValidationIssues(values) : [];
    $[22] = hasSubmitAttempted;
    $[23] = values;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const validationIssues = t8;
  const setTimeMenuStop = stopId => {
    timeMenuStopIdRef.current = stopId;
    setTimeMenuStopId(stopId);
  };
  const resetForm = () => {
    const nextValues = getQuickCreateInitialValues({
      currentUser,
      initialValues
    });
    setFormDefaults(nextValues);
    form.reset(nextValues);
    setActiveStopId(null);
    setCreatedCustomer(null);
    setCreatedEndCustomer(null);
    setCustomerPickerOpen(false);
    setCustomerSearch("");
    setEndCustomerPickerOpen(false);
    setEndCustomerSearch("");
    setTimeMenuStop(null);
    submitAttemptedRef.current = false;
    setHasSubmitAttempted(false);
    setHelpOpen(false);
    setDiscardConfirmationOpen(false);
    pendingPostCommitFocusRef.current = null;
    helpReturnFocusRef.current = null;
    cancelStopFocus();
  };
  const cancelStopFocus = () => {
    if (focusStopFrameRef.current !== null) {
      cancelAnimationFrame(focusStopFrameRef.current);
    }
    focusStopFrameRef.current = null;
  };
  const focusStop = stopId_0 => {
    cancelStopFocus();
    focusStopFrameRef.current = requestAnimationFrame(() => {
      focusStopFrameRef.current = null;
      document.getElementById(`quick-create-stop-${stopId_0}`)?.focus();
    });
  };
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  useEffect(() => cancelStopFocus, t9);
  const queuePostCommitFocus = resolve => {
    pendingPostCommitFocusRef.current = {
      phase: "before-popup-fallback",
      resolve
    };
  };
  const handleDialogFocusCapture = event => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target && !target.closest("[data-quick-create-stop]") && !isPopupSurface(target)) {
      setActiveStopId(null);
    }
    if (event.target !== event.currentTarget) {
      return;
    }
    const pendingFocus = pendingPostCommitFocusRef.current;
    const focusTarget = pendingFocus?.resolve();
    if (!pendingFocus || !focusTarget?.isConnected) {
      return;
    }
    if (pendingFocus.phase === "before-popup-fallback") {
      pendingFocus.phase = "after-popup-fallback";
      queueMicrotask(() => {
        const currentFocus = pendingPostCommitFocusRef.current;
        if (currentFocus !== pendingFocus || currentFocus.phase !== "after-popup-fallback") {
          return;
        }
        const currentTarget = currentFocus.resolve();
        if (currentTarget?.isConnected) {
          currentTarget.focus();
        }
      });
      return;
    }
    pendingPostCommitFocusRef.current = null;
    focusTarget.focus();
  };
  let t10;
  if ($[26] !== open) {
    t10 = [open];
    $[26] = open;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  useEffect(() => {
    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = open;
    if (!wasOpen && open || !open && wasOpen) {
      resetForm();
    }
  }, t10);
  let t11;
  let t12;
  if ($[28] !== form || $[29] !== requiresEndCustomer || $[30] !== values.requires_end_customer) {
    t11 = () => {
      if (requiresEndCustomer === values.requires_end_customer) {
        return;
      }
      form.setFieldValue("requires_end_customer", requiresEndCustomer);
    };
    t12 = [form, requiresEndCustomer, values.requires_end_customer];
    $[28] = form;
    $[29] = requiresEndCustomer;
    $[30] = values.requires_end_customer;
    $[31] = t11;
    $[32] = t12;
  } else {
    t11 = $[31];
    t12 = $[32];
  }
  useEffect(t11, t12);
  if (activeStopId !== null && !values.drops.some(stop => stop.id === activeStopId)) {
    setActiveStopId(null);
  }
  let t13;
  if ($[33] !== values.drops) {
    t13 = stopId_1 => values.drops.findIndex(stop_0 => stop_0.id === stopId_1);
    $[33] = values.drops;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const getStopIndex = t13;
  const navigateStop = (stopId_2, direction) => {
    const index = getStopIndex(stopId_2);
    const nextIndex = direction === "next" ? index + 1 : index - 1;
    const next = values.drops[nextIndex];
    if (next) {
      setActiveStopId(next.id);
      focusStop(next.id);
    }
  };
  const navigateStopFromDialog = direction_0 => {
    if (activeStopId) {
      navigateStop(activeStopId, direction_0);
      return true;
    }
    const boundaryIndex = direction_0 === "next" ? 0 : values.drops.length - 1;
    const boundaryStop = values.drops[boundaryIndex];
    if (!boundaryStop) {
      return false;
    }
    setActiveStopId(boundaryStop.id);
    focusStop(boundaryStop.id);
    return true;
  };
  const selectTimeTypeForStop = (stopId_3, type) => {
    if (pending || getStopIndex(stopId_3) < 0) {
      return;
    }
    const index_0 = getStopIndex(stopId_3);
    form.setFieldValue(`drops[${index_0}].time`, getStopTimeValue(type));
    setTimeMenuStop(null);
    requestAnimationFrame(() => {
      document.getElementById(`quick-create-time-${stopId_3}`)?.querySelector("input")?.focus();
    });
  };
  let t14;
  if ($[35] !== form || $[36] !== hasSubmitAttempted) {
    t14 = () => hasSubmitAttempted ? form.validate("submit") : undefined;
    $[35] = form;
    $[36] = hasSubmitAttempted;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const revalidateSubmittedForm = t14;
  let t15;
  if ($[38] !== form || $[39] !== revalidateSubmittedForm || $[40] !== values.drops) {
    t15 = (index_1, date) => {
      if (!date) {
        return;
      }
      values.drops.forEach((stop_1, stopIndex) => {
        if (stopIndex > index_1 && calendarDayNumber(stop_1.date) < calendarDayNumber(date)) {
          form.setFieldValue(`drops[${stopIndex}].date`, copyLocalCalendarDate(date));
        }
      });
      revalidateSubmittedForm();
    };
    $[38] = form;
    $[39] = revalidateSubmittedForm;
    $[40] = values.drops;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const moveFollowingStopDates = t15;
  const insertAfterStop = async stopId_4 => {
    const activeIndex = getStopIndex(stopId_4);
    if (activeIndex < 0) {
      return;
    }
    const nextStops = insertAfterActiveStop(values.drops, activeIndex);
    if (nextStops === values.drops) {
      return;
    }
    const insertedStop = nextStops.find(stop_2 => !values.drops.some(existing => existing.id === stop_2.id));
    const insertionIndex = activeIndex === values.drops.length - 1 ? activeIndex : activeIndex + 1;
    await form.insertFieldValue("drops", insertionIndex, nextStops[insertionIndex]);
    await revalidateSubmittedForm();
    if (insertedStop) {
      setActiveStopId(insertedStop.id);
      focusStop(insertedStop.id);
    }
  };
  const toggleStop = stopId_5 => {
    const index_2 = getStopIndex(stopId_5);
    if (index_2 < 0) {
      return;
    }
    const nextStops_0 = toggleStopType(values.drops, index_2);
    if (nextStops_0 === values.drops) {
      return;
    }
    if (!nextStops_0.every(isQuickCreateStop)) {
      return;
    }
    form.setFieldValue(`drops[${index_2}].type`, nextStops_0[index_2].type);
    setActiveStopId(stopId_5);
    focusStop(stopId_5);
  };
  const moveStopById = (stopId_6, direction_1) => {
    const index_3 = getStopIndex(stopId_6);
    if (index_3 < 0 || !canMoveStop(values.drops, index_3, direction_1)) {
      return;
    }
    const nextStops_1 = moveStop(values.drops, index_3, direction_1);
    if (nextStops_1 === values.drops) {
      return;
    }
    queuePostCommitFocus(() => document.getElementById(`quick-create-stop-${stopId_6}`));
    form.moveFieldValues("drops", index_3, direction_1 === "up" ? index_3 - 1 : index_3 + 1);
    revalidateSubmittedForm();
    setActiveStopId(stopId_6);
  };
  const deleteStopById = async stopId_7 => {
    const index_4 = getStopIndex(stopId_7);
    if (index_4 < 0 || !canDeleteStop(values.drops, index_4)) {
      return;
    }
    const nextStops_2 = deleteStop(values.drops, index_4);
    if (nextStops_2 === values.drops) {
      return;
    }
    const nextActiveStop = nextStops_2[index_4] ?? nextStops_2[index_4 - 1];
    await form.removeFieldValue("drops", index_4);
    await revalidateSubmittedForm();
    setActiveStopId(nextActiveStop?.id ?? null);
    if (nextActiveStop) {
      focusStop(nextActiveStop.id);
    }
  };
  let t16;
  if ($[42] !== form || $[43] !== onCustomerChange || $[44] !== revalidateSubmittedForm || $[45] !== values.company_id || $[46] !== values.drops) {
    t16 = company => {
      const changingCompany = values.company_id !== company.id;
      setCreatedCustomer(company);
      onCustomerChange?.(company);
      form.setFieldValue("company_id", company.id);
      form.setFieldValue("requires_end_customer", isThreePlBroker(company));
      if (!changingCompany) {
        return;
      }
      form.setFieldValue("end_customer_company_id", null);
      values.drops.forEach((_, index_5) => form.setFieldValue(`drops[${index_5}].place`, null));
      revalidateSubmittedForm();
    };
    $[42] = form;
    $[43] = onCustomerChange;
    $[44] = revalidateSubmittedForm;
    $[45] = values.company_id;
    $[46] = values.drops;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const setCustomer = t16;
  let t17;
  if ($[48] !== form || $[49] !== onCustomerChange || $[50] !== revalidateSubmittedForm || $[51] !== values.drops) {
    t17 = () => {
      setCreatedCustomer(null);
      onCustomerChange?.(null);
      setCreatedEndCustomer(null);
      form.setFieldValue("end_customer_company_id", null);
      form.setFieldValue("requires_end_customer", false);
      values.drops.forEach((__0, index_6) => form.setFieldValue(`drops[${index_6}].place`, null));
      revalidateSubmittedForm();
    };
    $[48] = form;
    $[49] = onCustomerChange;
    $[50] = revalidateSubmittedForm;
    $[51] = values.drops;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  const clearCustomer = t17;
  let t18;
  if ($[53] !== form || $[54] !== revalidateSubmittedForm || $[55] !== values.drops || $[56] !== values.end_customer_company_id) {
    t18 = company_0 => {
      const changingCompany_0 = values.end_customer_company_id !== company_0.id;
      setCreatedEndCustomer(company_0);
      form.setFieldValue("end_customer_company_id", company_0.id);
      if (changingCompany_0) {
        values.drops.forEach((__1, index_7) => form.setFieldValue(`drops[${index_7}].place`, null));
        revalidateSubmittedForm();
      }
    };
    $[53] = form;
    $[54] = revalidateSubmittedForm;
    $[55] = values.drops;
    $[56] = values.end_customer_company_id;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  const setEndCustomer = t18;
  let t19;
  if ($[58] !== form || $[59] !== revalidateSubmittedForm || $[60] !== values.drops) {
    t19 = () => {
      setCreatedEndCustomer(null);
      values.drops.forEach((__2, index_8) => form.setFieldValue(`drops[${index_8}].place`, null));
      revalidateSubmittedForm();
    };
    $[58] = form;
    $[59] = revalidateSubmittedForm;
    $[60] = values.drops;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  const clearEndCustomer = t19;
  let t20;
  if ($[62] !== isDirty || $[63] !== onOpenChange || $[64] !== pending) {
    t20 = () => {
      if (pending) {
        return;
      }
      if (isDirty) {
        setDiscardConfirmationOpen(true);
        return;
      }
      onOpenChange(false);
    };
    $[62] = isDirty;
    $[63] = onOpenChange;
    $[64] = pending;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  const requestClose = t20;
  const closeHelp = () => {
    const returnFocus = helpReturnFocusRef.current;
    if (returnFocus) {
      queuePostCommitFocus(() => returnFocus.isConnected ? returnFocus : null);
    }
    setHelpOpen(false);
  };
  const openHelp = () => {
    const activeElement = document.activeElement;
    helpReturnFocusRef.current = activeElement instanceof HTMLElement ? activeElement : null;
    setHelpOpen(true);
  };
  const toggleHelp = () => {
    if (helpOpen) {
      closeHelp();
    } else {
      openHelp();
    }
  };
  const focusFirstInvalidField = _temp4;
  const requestPublish = async () => {
    if (pending || isPublishingRef.current) {
      return;
    }
    const issues_0 = getQuickCreateValidationIssues(values);
    submitAttemptedRef.current = true;
    setHasSubmitAttempted(true);
    if (issues_0.length > 0) {
      await form.validate("submit");
      focusFirstInvalidField(issues_0);
      return;
    }
    await whileFlagged(isPublishingRef, () => form.handleSubmit());
  };
  const openTimeMenuForActiveStop = () => {
    if (pending) {
      return false;
    }
    const activeId = activeStopId ?? values.drops[0]?.id;
    if (!activeId || getStopIndex(activeId) < 0) {
      return false;
    }
    setTimeMenuStop(activeId);
    return true;
  };
  const openPickerForActiveStop = kind => {
    if (pending) {
      return false;
    }
    const activeId_0 = activeStopId ?? values.drops[0]?.id;
    if (!activeId_0) {
      return false;
    }
    const stop_3 = values.drops.find(candidate => candidate.id === activeId_0);
    if (!stop_3) {
      return false;
    }
    const trigger = kind === "date" ? dateTriggerRefs.current.get(activeId_0) : locationTriggerRefs.current.get(activeId_0);
    if (!trigger || trigger.disabled) {
      return false;
    }
    trigger.click();
    return true;
  };
  const focusQuickCreateField = _temp5;
  let t21;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = (path_0, trigger_0) => {
      if (!focusQuickCreateField(path_0) || !trigger_0 || trigger_0.disabled) {
        return false;
      }
      trigger_0.click();
      return true;
    };
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  const focusAndOpenTrigger = t21;
  const getArrowDirection = _temp6;
  const handleDialogKeyDown = event_1 => {
    if (event_1.repeat || event_1.nativeEvent.isComposing || event_1.key === "Process") {
      return;
    }
    if (discardConfirmationOpen) {
      return;
    }
    const target_2 = event_1.target instanceof HTMLElement ? event_1.target : null;
    if (target_2?.closest("[data-slot=\"sheet-content\"]")) {
      return;
    }
    if (event_1.key === "Escape" && isPopupSurface(target_2)) {
      return;
    }
    if (helpOpen && event_1.key === "Escape") {
      event_1.preventDefault();
      event_1.stopPropagation();
      closeHelp();
      return;
    }
    const timeMenuStopId_0 = timeMenuStopIdRef.current;
    const timeMenuType = !event_1.metaKey && !event_1.ctrlKey && !event_1.altKey && /^[1-5]$/.test(event_1.key) ? TIME_TYPES[Number(event_1.key) - 1] : undefined;
    if (timeMenuStopId_0 && timeMenuType) {
      event_1.preventDefault();
      event_1.stopPropagation();
      selectTimeTypeForStop(timeMenuStopId_0, timeMenuType);
      return;
    }
    const arrowDirection = getArrowDirection(event_1);
    if (arrowDirection) {
      if (!isArrowNavigationBlockedTarget(event_1.target) && navigateStopFromDialog(arrowDirection)) {
        event_1.preventDefault();
        event_1.stopPropagation();
      }
      return;
    }
    if (event_1.key !== "Escape") {
      return;
    }
    if (target_2?.closest("[data-quick-create-stop-header]")) {
      return;
    }
    event_1.preventDefault();
    event_1.stopPropagation();
    const field = target_2?.closest("[data-quick-create-focus^='drops.']");
    if (!field) {
      requestClose();
      return;
    }
    const stop_4 = field.closest("[data-quick-create-stop]");
    const header = stop_4?.querySelector("[data-quick-create-stop-header]");
    if (header) {
      focusStop(header.id.replace("quick-create-stop-", ""));
    }
  };
  let t22;
  if ($[67] !== discardConfirmationOpen) {
    const canRunQuickCreateShortcut = event_2 => {
      if (event_2.repeat || event_2.isComposing || discardConfirmationOpen) {
        return false;
      }
      if (!(event_2.target instanceof HTMLElement) || isPopupSurface(event_2.target)) {
        return false;
      }
      if (event_2.target.closest("[data-slot=\"sheet-content\"]")) {
        return false;
      }
      return !isTextEntryTarget(event_2.target) || event_2.ctrlKey || event_2.metaKey;
    };
    t22 = (event_3, action) => {
      if (!canRunQuickCreateShortcut(event_3)) {
        return;
      }
      if (action() !== false) {
        event_3.preventDefault();
      }
    };
    $[67] = discardConfirmationOpen;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  const runShortcut = t22;
  let t23;
  if ($[69] !== activeStopId || $[70] !== values.drops[0]?.id) {
    t23 = action_0 => {
      const activeId_1 = activeStopId ?? values.drops[0]?.id;
      return activeId_1 ? action_0(activeId_1) : false;
    };
    $[69] = activeStopId;
    $[70] = values.drops[0]?.id;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  const runForActiveStop = t23;
  let t24;
  if ($[72] !== activeStopId || $[73] !== getStopIndex) {
    t24 = () => {
      const activeIndex_0 = activeStopId ? getStopIndex(activeStopId) : 0;
      return focusQuickCreateField(`drops.${Math.max(activeIndex_0, 0)}.reference_no`);
    };
    $[72] = activeStopId;
    $[73] = getStopIndex;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  const focusActiveStopReference = t24;
  const publishShortcut = {
    callback: event_4 => runShortcut(event_4, requestPublish)
  };
  let t25;
  if ($[75] !== openTimeMenuForActiveStop || $[76] !== runShortcut) {
    t25 = event_7 => runShortcut(event_7, openTimeMenuForActiveStop);
    $[75] = openTimeMenuForActiveStop;
    $[76] = runShortcut;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== runShortcut) {
    t26 = event_12 => runShortcut(event_12, () => focusQuickCreateField("customer_rate_amount"));
    $[78] = runShortcut;
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  let t27;
  if ($[80] !== runShortcut) {
    t27 = event_13 => runShortcut(event_13, () => focusQuickCreateField("weight"));
    $[80] = runShortcut;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== focusActiveStopReference || $[83] !== runShortcut) {
    t28 = event_14 => runShortcut(event_14, focusActiveStopReference);
    $[82] = focusActiveStopReference;
    $[83] = runShortcut;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  let t29;
  if ($[85] !== runShortcut) {
    t29 = event_15 => runShortcut(event_15, () => focusQuickCreateField("notes"));
    $[85] = runShortcut;
    $[86] = t29;
  } else {
    t29 = $[86];
  }
  const hotkeys = [{
    callback: event_5 => runShortcut(event_5, () => openPickerForActiveStop("location")),
    hotkey: "Shift+L"
  }, {
    callback: event_6 => runShortcut(event_6, () => openPickerForActiveStop("date")),
    hotkey: "Shift+D"
  }, {
    callback: t25,
    hotkey: "Shift+T"
  }, {
    callback: event_8 => runShortcut(event_8, () => organisation.editable ? focusAndOpenTrigger("organisation_id", organisationTriggerRef.current) : focusQuickCreateField("organisation_id")),
    hotkey: "Shift+O"
  }, {
    callback: event_9 => runShortcut(event_9, () => focusAndOpenTrigger("company_id", customerTriggerRef.current)),
    hotkey: "Shift+C"
  }, {
    callback: event_10 => runShortcut(event_10, () => showEndCustomer && focusAndOpenTrigger("end_customer_company_id", endCustomerTriggerRef.current)),
    hotkey: "Shift+E"
  }, {
    callback: event_11 => runShortcut(event_11, () => focusAndOpenTrigger("load_type_id", vehicleTriggerRef.current)),
    hotkey: "Shift+V"
  }, {
    callback: t26,
    hotkey: "Shift+R"
  }, {
    callback: t27,
    hotkey: "Shift+W"
  }, {
    callback: t28,
    hotkey: "Shift+N"
  }, {
    callback: t29,
    hotkey: "Shift+M"
  }, {
    callback: event_16 => runShortcut(event_16, toggleHelp),
    hotkey: {
      key: "?"
    }
  }, {
    callback: event_17 => runShortcut(event_17, toggleHelp),
    hotkey: {
      key: "?",
      shift: true
    }
  }, {
    ...publishShortcut,
    hotkey: "Meta+Enter"
  }, {
    ...publishShortcut,
    hotkey: "Control+Enter"
  }];
  const sequences = [{
    callback: event_18 => runShortcut(event_18, () => runForActiveStop(insertAfterStop)),
    hotkey: ["S", "A"]
  }, {
    callback: event_19 => runShortcut(event_19, () => runForActiveStop(stopId_8 => moveStopById(stopId_8, "down"))),
    hotkey: ["S", "D"]
  }, {
    callback: event_20 => runShortcut(event_20, () => runForActiveStop(toggleStop)),
    hotkey: ["S", "T"]
  }, {
    callback: event_21 => runShortcut(event_21, () => runForActiveStop(stopId_9 => moveStopById(stopId_9, "up"))),
    hotkey: ["S", "U"]
  }, {
    callback: event_22 => runShortcut(event_22, () => runForActiveStop(deleteStopById)),
    hotkey: ["S", "X"]
  }];
  const T0 = form.AppForm;
  let t30;
  if ($[87] !== onOpenChange || $[88] !== requestClose) {
    t30 = nextOpen => nextOpen ? onOpenChange(true) : requestClose();
    $[87] = onOpenChange;
    $[88] = requestClose;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  let t31;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <DialogTitle className="text-2xl font-extrabold" data-sentry-element="DialogTitle" data-sentry-source-file="quick-create-load-dialog.tsx">Create load</DialogTitle>;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  let t32;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <X data-sentry-element="X" data-sentry-source-file="quick-create-load-dialog.tsx" />;
    $[91] = t32;
  } else {
    t32 = $[91];
  }
  let t33;
  if ($[92] !== requestClose) {
    t33 = <DialogHeader className="flex-row items-center justify-between gap-4" data-sentry-element="DialogHeader" data-sentry-source-file="quick-create-load-dialog.tsx">{t31}<Button aria-label="Close load dialog" onClick={requestClose} size="icon-sm" type="button" variant="ghost" data-sentry-element="Button" data-sentry-source-file="quick-create-load-dialog.tsx">{t32}</Button></DialogHeader>;
    $[92] = requestClose;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] !== validationIssues[0]?.message || $[95] !== validationIssues.length) {
    t34 = validationIssues.length > 0 ? <div aria-live="assertive" className="animate-in rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm duration-150 ease-out fade-in-0" role="alert"><p className="font-medium">Fix before creating: {validationIssues[0]?.message}</p>{validationIssues.length > 1 ? <p className="mt-1 text-muted-foreground">{validationIssues.length - 1} more field{validationIssues.length === 2 ? "" : "s"} need attention.</p> : null}</div> : null;
    $[94] = validationIssues[0]?.message;
    $[95] = validationIssues.length;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  let t35;
  if ($[97] !== publishError) {
    t35 = publishError ? <div aria-live="assertive" className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm" role="alert">{publishError}</div> : null;
    $[97] = publishError;
    $[98] = t35;
  } else {
    t35 = $[98];
  }
  const t36 = "quick-create-stops-heading";
  const t37 = "space-y-2";
  const t38 = "drops";
  const t39 = -1;
  let t40;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="font-medium" id="quick-create-stops-heading">Stops</h2></div>;
    $[99] = t40;
  } else {
    t40 = $[99];
  }
  let t41;
  if ($[100] !== form.Field) {
    t41 = <form.Field name="drops" data-sentry-element="form.Field" data-sentry-source-file="quick-create-load-dialog.tsx">{_temp11}</form.Field>;
    $[100] = form.Field;
    $[101] = t41;
  } else {
    t41 = $[101];
  }
  const t42 = values.drops.map((stop_5, index_10) => <Fragment key={stop_5.id}><QuickCreateStopField actions={index_10 > 0 && index_10 < values.drops.length - 1 ? {
      canDelete: canDeleteStop(values.drops, index_10),
      canMoveDown: canMoveStop(values.drops, index_10, "down"),
      canMoveUp: canMoveStop(values.drops, index_10, "up"),
      onDelete: () => deleteStopById(stop_5.id),
      onMoveDown: () => moveStopById(stop_5.id, "down"),
      onMoveUp: () => moveStopById(stop_5.id, "up"),
      onToggleType: () => toggleStop(stop_5.id)
    } : undefined} active={activeStopId === stop_5.id} companyId={locationCompanyId} dateTriggerRef={node => {
      if (node) {
        dateTriggerRefs.current.set(stop_5.id, node);
      } else {
        dateTriggerRefs.current.delete(stop_5.id);
      }
    }} fields={`drops[${index_10}]`} form={form} key={`drops[${index_10}]`} locationTriggerRef={node_0 => {
      if (node_0) {
        locationTriggerRefs.current.set(stop_5.id, node_0);
      } else {
        locationTriggerRefs.current.delete(stop_5.id);
      }
    }} minDate={getEarliestAllowedDate(values.drops, index_10)} nearbyCoordinates={getRouteStopBiasCoordinates(values.drops, index_10)} onCreateLocation={onCreateLocation} onDateChange={date_0 => moveFollowingStopDates(index_10, date_0)} onEditLocation={onEditLocation} onFocusStop={() => setActiveStopId(stop_5.id)} onOpenTimeMenuChange={nextOpen_0 => setTimeMenuStop(nextOpen_0 ? stop_5.id : null)} onSelectTimeType={type_0 => selectTimeTypeForStop(stop_5.id, type_0)} organisationId={values.organisation_id ?? undefined} pending={pending} stopId={stop_5.id} stopNumber={index_10 + 1} timeMenuOpen={timeMenuStopId === stop_5.id} timeTypes={TIME_TYPES} />{index_10 < values.drops.length - 1 ? <StopGap afterStopNumber={index_10 + 2} beforeStopNumber={index_10 + 1} disabled={pending} onAdd={() => insertAfterStop(stop_5.id)} /> : null}</Fragment>);
  let t43;
  if ($[102] !== t40 || $[103] !== t41 || $[104] !== t42) {
    t43 = <section aria-labelledby={t36} className={t37} data-quick-create-focus={t38} tabIndex={t39}>{t40}{t41}{t42}</section>;
    $[102] = t40;
    $[103] = t41;
    $[104] = t42;
    $[105] = t43;
  } else {
    t43 = $[105];
  }
  let t44;
  if ($[106] !== pending || $[107] !== vehicleTriggerRef) {
    t44 = field_2 => <field_2.VehicleTypeInput disabled={pending} label={<span className="flex items-center gap-2"><span>Vehicle type *</span><Kbd>⇧V</Kbd></span>} placeholder="Select vehicle type" triggerRef={vehicleTriggerRef} />;
    $[106] = pending;
    $[107] = vehicleTriggerRef;
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  let t45;
  if ($[109] !== form.AppField || $[110] !== t44) {
    t45 = <div className="min-w-0" data-quick-create-focus="load_type_id"><form.AppField name="load_type_id" data-sentry-element="form.AppField" data-sentry-source-file="quick-create-load-dialog.tsx">{t44}</form.AppField></div>;
    $[109] = form.AppField;
    $[110] = t44;
    $[111] = t45;
  } else {
    t45 = $[111];
  }
  let t46;
  if ($[112] !== form || $[113] !== values.weight_unit) {
    t46 = field_3 => <field_3.WeightInput className="w-full! [&>[data-slot=select-trigger]]:flex-1 [&>[data-slot=select-trigger]]:not-data-disabled:hover:bg-muted" compact={true} inputRef={weightInputRef} label={<span className="flex items-center gap-2"><span>Weight (optional)</span><Kbd>⇧W</Kbd></span>} onUnitChange={unit => form.setFieldValue("weight_unit", unit)} portalUnitSelect={true} unit={values.weight_unit} />;
    $[112] = form;
    $[113] = values.weight_unit;
    $[114] = t46;
  } else {
    t46 = $[114];
  }
  let t47;
  if ($[115] !== form.AppField || $[116] !== t46) {
    t47 = <div data-quick-create-focus="weight"><form.AppField name="weight" data-sentry-element="form.AppField" data-sentry-source-file="quick-create-load-dialog.tsx">{t46}</form.AppField></div>;
    $[115] = form.AppField;
    $[116] = t46;
    $[117] = t47;
  } else {
    t47 = $[117];
  }
  let t48;
  if ($[118] !== pending || $[119] !== t47) {
    t48 = <fieldset className="min-w-0" disabled={pending}>{t47}</fieldset>;
    $[118] = pending;
    $[119] = t47;
    $[120] = t48;
  } else {
    t48 = $[120];
  }
  let t49;
  if ($[121] !== form.Field || $[122] !== pending) {
    t49 = field_4 => <Field className="min-w-0 gap-1" data-quick-create-focus="customer_rate_amount"><FieldLabel htmlFor="quick-create-customer-rate">Customer rate <Kbd>⇧R</Kbd></FieldLabel><FieldContent><ButtonGroup className="w-full"><Input className="w-full" disabled={pending} id="quick-create-customer-rate" inputMode="decimal" min="0" onBlur={field_4.handleBlur} onChange={event_24 => field_4.handleChange(event_24.target.value)} ref={rateInputRef} step="0.01" type="number" value={field_4.state.value} /><form.Field name="customer_rate_currency">{currencyField => <Suspense fallback={<Skeleton className="h-9 w-24" />}><QuickCreateCurrencySelect disabled={pending} onChange={currencyField.handleChange} value={currencyField.state.value} /></Suspense>}</form.Field></ButtonGroup><FieldError errors={field_4.state.meta.errors} /></FieldContent></Field>;
    $[121] = form.Field;
    $[122] = pending;
    $[123] = t49;
  } else {
    t49 = $[123];
  }
  let t50;
  if ($[124] !== form.Field || $[125] !== t49) {
    t50 = <form.Field name="customer_rate_amount" data-sentry-element="form.Field" data-sentry-source-file="quick-create-load-dialog.tsx">{t49}</form.Field>;
    $[124] = form.Field;
    $[125] = t49;
    $[126] = t50;
  } else {
    t50 = $[126];
  }
  let t51;
  if ($[127] !== t45 || $[128] !== t48 || $[129] !== t50) {
    t51 = <div className="grid min-w-0 gap-3 @min-[44rem]/quick-create:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.5fr)]">{t45}{t48}{t50}</div>;
    $[127] = t45;
    $[128] = t48;
    $[129] = t50;
    $[130] = t51;
  } else {
    t51 = $[130];
  }
  let t52;
  if ($[131] !== pending) {
    t52 = field_5 => <field_5.Textarea disabled={pending} label={<FieldLabel htmlFor="notes">Customer cargo notes (optional) <Kbd>⇧M</Kbd></FieldLabel>} placeholder="Additional information" rows={3} />;
    $[131] = pending;
    $[132] = t52;
  } else {
    t52 = $[132];
  }
  let t53;
  if ($[133] !== form.AppField || $[134] !== t52) {
    t53 = <div data-quick-create-focus="notes"><form.AppField name="notes" data-sentry-element="form.AppField" data-sentry-source-file="quick-create-load-dialog.tsx">{t52}</form.AppField></div>;
    $[133] = form.AppField;
    $[134] = t52;
    $[135] = t53;
  } else {
    t53 = $[135];
  }
  let t54;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <CircleHelp data-sentry-element="CircleHelp" data-sentry-source-file="quick-create-load-dialog.tsx" />;
    $[136] = t54;
  } else {
    t54 = $[136];
  }
  let t55;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <Kbd data-sentry-element="Kbd" data-sentry-source-file="quick-create-load-dialog.tsx">?</Kbd>;
    $[137] = t55;
  } else {
    t55 = $[137];
  }
  let t56;
  if ($[138] !== toggleHelp) {
    t56 = <Button aria-label="Keyboard shortcuts" className="hidden sm:inline-flex" onClick={toggleHelp} type="button" variant="ghost" data-sentry-element="Button" data-sentry-source-file="quick-create-load-dialog.tsx">{t54}Shortcuts{t55}</Button>;
    $[138] = toggleHelp;
    $[139] = t56;
  } else {
    t56 = $[139];
  }
  let t57;
  if ($[140] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <span className="inline-flex items-center gap-1"><KbdGroup data-sentry-element="KbdGroup" data-sentry-source-file="quick-create-load-dialog.tsx"><Kbd data-sentry-element="Kbd" data-sentry-source-file="quick-create-load-dialog.tsx">Tab</Kbd><Kbd data-sentry-element="Kbd" data-sentry-source-file="quick-create-load-dialog.tsx">Shift+Tab</Kbd></KbdGroup>Move</span>;
    $[140] = t57;
  } else {
    t57 = $[140];
  }
  let t58;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <div className="hidden min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground sm:flex">{t57}<span className="inline-flex items-center gap-1"><Kbd data-sentry-element="Kbd" data-sentry-source-file="quick-create-load-dialog.tsx">Esc</Kbd>Back</span></div>;
    $[141] = t58;
  } else {
    t58 = $[141];
  }
  let t59;
  if ($[142] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <KbdGroup data-sentry-element="KbdGroup" data-sentry-source-file="quick-create-load-dialog.tsx"><Kbd data-sentry-element="Kbd" data-sentry-source-file="quick-create-load-dialog.tsx">⌘</Kbd><Kbd data-sentry-element="Kbd" data-sentry-source-file="quick-create-load-dialog.tsx">↵</Kbd></KbdGroup>;
    $[142] = t59;
  } else {
    t59 = $[142];
  }
  let t60;
  if ($[143] !== pending) {
    t60 = <Button disabled={pending} type="submit" data-sentry-element="Button" data-sentry-source-file="quick-create-load-dialog.tsx">Create Load to cover{t59}</Button>;
    $[143] = pending;
    $[144] = t60;
  } else {
    t60 = $[144];
  }
  let t61;
  if ($[145] !== t56 || $[146] !== t60) {
    t61 = <DialogFooter className="shrink-0 flex-wrap items-center gap-2" data-sentry-element="DialogFooter" data-sentry-source-file="quick-create-load-dialog.tsx">{t56}{t58}{t60}</DialogFooter>;
    $[145] = t56;
    $[146] = t60;
    $[147] = t61;
  } else {
    t61 = $[147];
  }
  let t62;
  if ($[148] !== closeHelp || $[149] !== helpOpen) {
    t62 = helpOpen ? <aside aria-label="Keyboard shortcuts" className="w-full shrink-0 border-t pt-4 md:w-64 md:border-t-0 md:border-l md:pt-0 md:pl-4" tabIndex={-1}><div><div className="flex items-center justify-between gap-2"><h2 className="font-medium">Keyboard shortcuts</h2><Button aria-label="Hide shortcuts" onClick={closeHelp} size="sm" type="button" variant="ghost">Hide shortcuts</Button></div></div><p className="mt-1 text-sm text-muted-foreground">Available while the dialog is focused.</p><dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">{[["\u21E7O", "Organisation"], ["\u21E7C", "Customer"], ["\u21E7E", "End customer"], ["\u21E7L", "Stop location"], ["\u21E7D", "Stop date"], ["\u21E7T", "Stop time type"], ["\u21E7N", "Stop reference"], ["\u21E7V", "Vehicle type"], ["\u21E7W", "Weight"], ["\u21E7R", "Customer rate"], ["\u21E7M", "Customer cargo notes"], ["SA", "Insert stop"], ["ST", "Toggle intermediate stop type"], ["SU / SD", "Move stop"], ["SX", "Delete stop"], ["\u2191 / \u2193", "Navigate stops"], ["Esc", "Return focus / close"]].map(_temp12)}</dl></aside> : null;
    $[148] = closeHelp;
    $[149] = helpOpen;
    $[150] = t62;
  } else {
    t62 = $[150];
  }
  let t63;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = nextOpen_1 => {
      if (!nextOpen_1) {
        setDiscardConfirmationOpen(false);
      }
    };
    $[151] = t63;
  } else {
    t63 = $[151];
  }
  let t64;
  if ($[152] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <AlertDialogHeader data-sentry-element="AlertDialogHeader" data-sentry-source-file="quick-create-load-dialog.tsx"><AlertDialogTitle data-sentry-element="AlertDialogTitle" data-sentry-source-file="quick-create-load-dialog.tsx">Discard this new load?</AlertDialogTitle><AlertDialogDescription data-sentry-element="AlertDialogDescription" data-sentry-source-file="quick-create-load-dialog.tsx">Your unsaved load details will be lost.</AlertDialogDescription></AlertDialogHeader>;
    $[152] = t64;
  } else {
    t64 = $[152];
  }
  let t65;
  if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <AlertDialogCancel onClick={() => setDiscardConfirmationOpen(false)} data-sentry-element="AlertDialogCancel" data-sentry-source-file="quick-create-load-dialog.tsx">Keep editing</AlertDialogCancel>;
    $[153] = t65;
  } else {
    t65 = $[153];
  }
  let t66;
  if ($[154] !== onOpenChange || $[155] !== resetForm) {
    t66 = <AlertDialogContent data-sentry-element="AlertDialogContent" data-sentry-source-file="quick-create-load-dialog.tsx">{t64}<AlertDialogFooter data-sentry-element="AlertDialogFooter" data-sentry-source-file="quick-create-load-dialog.tsx">{t65}<AlertDialogAction onClick={() => {
          setDiscardConfirmationOpen(false);
          resetForm();
          onOpenChange(false);
        }} type="button" data-sentry-element="AlertDialogAction" data-sentry-source-file="quick-create-load-dialog.tsx">Discard changes</AlertDialogAction></AlertDialogFooter></AlertDialogContent>;
    $[154] = onOpenChange;
    $[155] = resetForm;
    $[156] = t66;
  } else {
    t66 = $[156];
  }
  let t67;
  if ($[157] !== discardConfirmationOpen || $[158] !== t66) {
    t67 = <AlertDialog onOpenChange={t63} open={discardConfirmationOpen} data-sentry-element="AlertDialog" data-sentry-source-file="quick-create-load-dialog.tsx">{t66}</AlertDialog>;
    $[157] = discardConfirmationOpen;
    $[158] = t66;
    $[159] = t67;
  } else {
    t67 = $[159];
  }
  return <T0 data-sentry-element="form.AppForm" data-sentry-component="QuickCreateLoadDialog" data-sentry-source-file="quick-create-load-dialog.tsx"><Dialog onOpenChange={t30} open={open} data-sentry-element="Dialog" data-sentry-source-file="quick-create-load-dialog.tsx"><DialogContent className="top-[5dvh] flex max-h-[90dvh] translate-y-0 flex-col overflow-hidden data-closed:animate-none! data-open:animate-none! sm:max-w-5xl md:flex-row" data-shortcut-scope={true} onFocusCapture={handleDialogFocusCapture} onKeyDownCapture={handleDialogKeyDown} ref={dialogRef} showCloseButton={false} data-sentry-element="DialogContent" data-sentry-source-file="quick-create-load-dialog.tsx"><QuickCreateHotkeys hotkeys={hotkeys} sequences={sequences} target={dialogRef} data-sentry-element="QuickCreateHotkeys" data-sentry-source-file="quick-create-load-dialog.tsx" /><form className="@container/quick-create flex min-h-0 flex-1 flex-col gap-4" onSubmit={event_23 => {
          event_23.preventDefault();
          requestPublish();
        }}><div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1" data-quick-create-scroll-body={true}>{t33}{t34}{t35}<div className="grid gap-3 md:grid-cols-[1fr_2fr]"><form.Field name="organisation_id" data-sentry-element="form.Field" data-sentry-source-file="quick-create-load-dialog.tsx">{field_0 => {
                  const selectedOrganisation = organisation.options.find(option => option.id === field_0.state.value);
                  return <Field className="gap-1" data-quick-create-focus="organisation_id"><FieldLabel>Organisation * <Kbd>⇧O</Kbd></FieldLabel><FieldContent>{organisation.editable ? <Select disabled={pending} items={organisation.options.map(_temp7)} onValueChange={value_1 => {
                        if (!value_1) {
                          return;
                        }
                        const nextOrganisationId = Number(value_1);
                        if (nextOrganisationId === field_0.state.value) {
                          return;
                        }
                        field_0.handleChange(nextOrganisationId);
                        form.setFieldValue("company_id", null);
                        form.setFieldValue("end_customer_company_id", null);
                        values.drops.forEach((__3, index_9) => form.setFieldValue(`drops[${index_9}].place`, null));
                        revalidateSubmittedForm();
                        onCustomerChange?.(null);
                        onOrganisationChange?.(nextOrganisationId);
                      }} value={field_0.state.value?.toString() ?? ""}><SelectTrigger aria-invalid={field_0.state.meta.errors.length > 0} aria-label="Organisation" className="not-data-disabled:hover:bg-muted" ref={organisationTriggerRef}><SelectValue placeholder="Select organisation" /></SelectTrigger><SelectPortal><SelectContent>{organisation.options.map(_temp8)}</SelectContent></SelectPortal></Select> : <Input aria-invalid={field_0.state.meta.errors.length > 0} aria-label="Organisation" readOnly={true} value={selectedOrganisation?.name ?? "Organisation unavailable"} />}<FieldError errors={field_0.state.meta.errors} /></FieldContent></Field>;
                }}</form.Field><div className="flex min-w-0 flex-col gap-3"><div data-quick-create-focus="company_id"><form.AppField name="company_id" data-sentry-element="form.AppField" data-sentry-source-file="quick-create-load-dialog.tsx">{() => <FormCompanyPicker companies={customerCompanies.companies} description={values.organisation_id == null ? "Select organisation first" : "Find a customer or create a new company."} disabled={pending || values.organisation_id == null} error={customerCompanies.error} label={<span>Customer * <Kbd>⇧C</Kbd></span>} onClear={clearCustomer} onCreateCompany={onCreateCompany && values.organisation_id != null ? search => onCreateCompany({
                      kind: "customer",
                      onCreated: setCustomer,
                      organisationId: values.organisation_id,
                      search
                    }) : undefined} onOpenChange={setCustomerPickerOpen} onRetry={customerCompanies.onRetry ?? _temp9} onSearchChange={search_0 => {
                      setCustomerSearch(search_0);
                      customerCompanies.onSearchChange?.(search_0);
                    }} onSelect={setCustomer} open={customerPickerOpen} placeholder={requiresEndCustomer ? "Select company" : "Select customer"} search={customerSearch} searchPlaceholder="Search customer companies..." selectedCompany={selectedCustomer} status={customerCompanies.status ?? "ready"} triggerRef={customerTriggerRef} triggerStyle="search" />}</form.AppField></div>{showEndCustomer ? <div data-quick-create-focus="end_customer_company_id"><form.AppField name="end_customer_company_id">{() => <FormCompanyPicker companies={endCustomerCompanies.companies} description="Choose the company that actually ships this load." disabled={pending || values.company_id == null} error={endCustomerCompanies.error} label={<span className="flex items-center gap-2"><span>End Customer{requiresEndCustomer ? " *" : ""}</span><Kbd>⇧E</Kbd></span>} onClear={clearEndCustomer} onCreateCompany={onCreateCompany && values.organisation_id != null ? search_1 => onCreateCompany({
                      kind: "end-customer",
                      onCreated: setEndCustomer,
                      organisationId: values.organisation_id,
                      search: search_1
                    }) : undefined} onOpenChange={setEndCustomerPickerOpen} onRetry={endCustomerCompanies.onRetry ?? _temp10} onSearchChange={search_2 => {
                      setEndCustomerSearch(search_2);
                      endCustomerCompanies.onSearchChange?.(search_2);
                    }} onSelect={setEndCustomer} open={endCustomerPickerOpen} placeholder="Select end customer" search={endCustomerSearch} searchPlaceholder="Search end customer companies..." selectedCompany={selectedEndCustomer} status={endCustomerCompanies.status ?? "ready"} triggerRef={endCustomerTriggerRef} triggerStyle="search" />}</form.AppField></div> : null}</div></div>{customerRiskBanner}{t43}{t51}{t53}</div>{t61}</form>{t62}{nestedSheetOutlets}</DialogContent></Dialog>{t67}</T0>;
};
function _temp(currency) {
  return <SelectItem key={currency.alias} value={currency.iso_code}>{currency.iso_code}</SelectItem>;
}
function _temp2(state) {
  return state.values;
}
function _temp3(state_0) {
  return state_0.isDirty;
}
function _temp4(issues) {
  const issue = issues[0];
  if (!issue) {
    return;
  }
  requestAnimationFrame(() => {
    const target_0 = document.querySelector(`[data-quick-create-focus="${issue.path}"]`);
    const control = target_0?.querySelector("input:not(:disabled), textarea:not(:disabled)") ?? target_0?.querySelector("button:not(:disabled), [role=\"combobox\"]");
    (control ?? target_0)?.focus();
  });
}
function _temp5(path) {
  const target_1 = document.querySelector(`[data-quick-create-focus="${path}"]`);
  const control_0 = target_1?.querySelector("input:not(:disabled), textarea:not(:disabled), button:not(:disabled), [role=combobox]");
  if (!control_0) {
    return false;
  }
  control_0.focus();
  return true;
}
function _temp6(event_0) {
  if (event_0.metaKey || event_0.ctrlKey || event_0.altKey || event_0.shiftKey) {
    return;
  }
  if (event_0.key === "ArrowDown") {
    return "next";
  }
  if (event_0.key === "ArrowUp") {
    return "previous";
  }
}
function _temp7(option_0) {
  return {
    label: option_0.name,
    value: String(option_0.id)
  };
}
function _temp8(option_1) {
  return <SelectItem key={option_1.id} value={String(option_1.id)}>{option_1.name}</SelectItem>;
}
function _temp9() {}
function _temp10() {}
function _temp11(field_1) {
  return <FieldError errors={field_1.state.meta.errors} />;
}
function _temp12(t0) {
  const [shortcut, description] = t0;
  return <Fragment key={shortcut}><dt><Kbd>{shortcut}</Kbd></dt><dd>{description}</dd></Fragment>;
}